// src/pages/EmergencyContributions.jsx
import React, { useMemo, useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Card, CardContent, Grid, Button, Chip, IconButton,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  Divider, Tooltip, Stepper, Step, StepLabel, Stack, InputAdornment
} from "@mui/material";
import { motion } from "framer-motion";
import AddIcon from "@mui/icons-material/Add";
import InfoIcon from "@mui/icons-material/Info";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import DoNotDisturbOnIcon from "@mui/icons-material/DoNotDisturbOn";
import GavelIcon from "@mui/icons-material/Gavel";
import PersonIcon from "@mui/icons-material/Person";
import GroupIcon from "@mui/icons-material/Group";
import EventIcon from "@mui/icons-material/Event";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import CloseIcon from "@mui/icons-material/Close";
import Swal from "sweetalert2";
import {
  fetchEmergencyEvents,
  createEmergencyEvent,
  reviewEmergencyEvent,
} from "../services/emergencyService";
import LoadingSpinner from "../components/LoadingSpinner"; // Assuming you have this
import { fetchMembers } from "../services/memberService"; // Assuming you have this
import { useAuth } from "../contexts/AuthContext";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import formatCurrency from "../shared/formatCurrency";


const brand = "#800b33";
const brandHover = "#6c0a2b";

const fadeIn = {
  hidden: { opacity: 0, y: 18 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.06, duration: 0.35, ease: "easeOut" },
  }),
};

const statusChip = (status) => {
  switch (status) {
    case "PENDING_SECRETARY_APPROVAL": return { label: "Pending Secretary", color: "warning", icon: <PendingActionsIcon fontSize="small" /> };
    case "PENDING_ACCOUNTANT_APPROVAL": return { label: "Pending Accountant", color: "warning", icon: <PendingActionsIcon fontSize="small" /> };
    case "COMPLETED": return { label: "Completed", color: "success", icon: <CheckCircleIcon fontSize="small" /> };
    case "REJECTED": return { label: "Rejected", color: "error", icon: <DoNotDisturbOnIcon fontSize="small" /> };
    default: return { label: status, color: "default" };
  }
};

const steps = ["Mwenyekiti", "Katibu", "Muhasibu"];
const getActiveStep = (status) => {
  switch (status) {
    case "PENDING_SECRETARY_APPROVAL": return 0; // after chair created
    case "PENDING_ACCOUNTANT_APPROVAL": return 1; // after katibu
    case "COMPLETED": return 2; // done
    case "REJECTED": return 0;
    default: return 0;
  }
};




// ====== MAIN PAGE ======
export default function EmergencyContributions() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === "sw" ? "sw-TZ" : "en-TZ";
  const { role: currentRole, loading: authLoading } = useAuth();

  const [members, setMembers] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [initiateOpen, setInitiateOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);

  // Initiate form
  const [form, setForm] = useState({ type: "Bereavement", title: "", deductionPerMember: 10000 });

  const canInitiate = currentRole === "Mwenyekiti";
  const canKatibuApprove = currentRole === "Katibu";
  const canMuhasibuApprove = currentRole === "Muhasibu";

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [eventsData, membersData] = await Promise.all([
        fetchEmergencyEvents(),
        fetchMembers(),
      ]);
      setEvents(eventsData);
      setMembers(membersData);
    } catch (err) {
      setError(err.message);
      setError(err.message);
      Swal.fire({ icon: "error", title: t("Failed to load data"), text: err.message, confirmButtonColor: brand });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const totals = useMemo(() => {
    const totalActive = members.filter(m => m.is_active).length;
    const totalOutstanding = members.reduce((acc, m) => acc + m.balance, 0);
    return { totalActive, totalOutstanding };
  }, [members]);

  // ====== Actions ======
  const openDetails = (ev) => { setSelectedEvent(ev); setDetailsOpen(true); };
  const closeDetails = () => { setSelectedEvent(null); setDetailsOpen(false); };

  const handleInitiate = async () => {
    if (!form.title || !form.deductionPerMember || form.deductionPerMember <= 0) {
      Swal.fire({ icon: "warning", title: t("Missing data"), text: t("Please fill event title and a valid deduction amount."), confirmButtonColor: brand });
      return;
    }
    try {
      await createEmergencyEvent(form);
      setInitiateOpen(false);
      Swal.fire({ icon: "success", title: t("Emergency created"), text: t("Pending Secretary approval."), confirmButtonColor: brand });
      loadData(); // Refresh the list
    } catch (err) {
      Swal.fire({ icon: "error", title: t("Creation Failed"), text: err.message, confirmButtonColor: brand });
    }
  };

  const handleReview = async (event, isApprove, role) => {
    const actionText = isApprove ? t("Approve") : t("Reject"); // Note: Using simple translation for button text logic
    const swalResult = await Swal.fire({
      title: t("Approve emergency?"),
      text: isApprove ? t("This will forward to the next stage.") : t("This action cannot be undone."),
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: actionText,
      cancelButtonText: t("Cancel"),
      confirmButtonColor: isApprove ? brand : "#c62828",
      input: isApprove ? undefined : "text",
      inputLabel: isApprove ? undefined : t("Reason for rejection (optional)"),
    });

    if (!swalResult.isConfirmed) return;

    try {
      await reviewEmergencyEvent(event.id, isApprove, swalResult.value || "");
      Swal.fire({ icon: "success", title: t("Action Complete"), text: isApprove ? t("Event has been approved.") : t("Event has been rejected."), confirmButtonColor: brand });
      loadData();
    } catch (err) {
      Swal.fire({ icon: "error", title: t("Action Failed"), text: err.message, confirmButtonColor: brand });
    }
  };

  // ====== helpers ======
  function nowStr() { return new Date().toISOString().slice(0, 16).replace("T", " "); }

  // ====== Derived table view (you can add filters later) ======
  const rows = events;

  if (loading || authLoading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return <Typography color="error" align="center" sx={{ my: 4 }}>Error: {error}</Typography>;
  }


  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      {/* Header */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 900, color: brand }}>
          {t("Emergency Contributions")}
        </Typography>
        <Typography variant="subtitle2" color="text.secondary">
          {t("Chairperson initiates → Katibu reviews → Muhasibu approves & deducts")}
        </Typography>
      </Box>

      {/* Top summary cards */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={4}>
          <motion.div variants={fadeIn} initial="hidden" animate="visible">
            <Card sx={{ p: 2, borderRadius: 3, boxShadow: 3 }}>
              <Stack direction="row" spacing={2} alignItems="center">
                <GroupIcon sx={{ color: brand }} />
                <Box>
                  <Typography variant="body2" color="text.secondary">{t("Active Members")}</Typography>
                  <Typography variant="h6" fontWeight={800}>{totals.totalActive}</Typography>
                </Box>
              </Stack>
            </Card>
          </motion.div>
        </Grid>
        <Grid item xs={12} md={4}>
          <motion.div variants={fadeIn} initial="hidden" animate="visible">
            <Card sx={{ p: 2, borderRadius: 3, boxShadow: 3 }}>
              <Stack direction="row" spacing={2} alignItems="center">
                <EventIcon sx={{ color: brand }} />
                <Box>
                  <Typography variant="body2" color="text.secondary">{t("Open Emergencies")}</Typography>
                  <Typography variant="h6" fontWeight={800}>{events.filter(e => e.status.startsWith("PENDING_")).length}
                  </Typography>
                </Box>
              </Stack>
            </Card>
          </motion.div>
        </Grid>
        {/* <Grid item xs={12} md={4}>
          <motion.div variants={fadeIn} initial="hidden" animate="visible">
            <Card sx={{ p: 2, borderRadius: 3, boxShadow: 3 }}>
              <Stack direction="row" spacing={2} alignItems="center">
                <PersonIcon sx={{ color: brand }} />
                <Box>
                  <Typography variant="body2" color="text.secondary">Total Outstanding (TZS)</Typography>
                  <Typography variant="h6" fontWeight={800}>{formatTZS(totals.totalOutstanding)}</Typography>
                </Box>
              </Stack>
            </Card>
          </motion.div>
        </Grid> */}
      </Grid>

      {/* Toolbar */}
      <Card sx={{ borderRadius: 3, boxShadow: 3, mb: 2 }}>
        <CardContent sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>{t("Event List")}</Typography>
          {canInitiate && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setInitiateOpen(true)}
              sx={{ bgcolor: brand, "&:hover": { bgcolor: brandHover } }}
            >
              {t("Initiate Emergency")}
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Table */}
      <motion.div variants={fadeIn} initial="hidden" animate="visible">
        <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
          <CardContent sx={{ p: 0 }}>
            <TableContainer component={Paper} elevation={0}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{t("Event")}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{t("Type")}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{t("Deduction / Member")}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{t("Initiated By")}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{t("Status")}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{t("Approval Chain")}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{t("Actions")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 6, color: "text.secondary" }}>
                        {t("No emergency events found.")}
                      </TableCell>
                    </TableRow>
                  ) : (

                    rows.map((r, idx) => {
                      const s = statusChip(r.status);
                      const activeStep = getActiveStep(r.status);
                      return (
                        <TableRow key={r.id} hover>
                          <TableCell>{r.id}</TableCell>
                          <TableCell>{r.event_name}</TableCell>
                          <TableCell>{r.event_type}</TableCell>
                          <TableCell>{formatCurrency(r.amount_per_member, locale)}</TableCell>
                          <TableCell>{r.initiated_by?.full_name}-{r.initiated_by?.role || r.initiated_by?.vikoba_role}</TableCell>
                          <TableCell>
                            <Chip icon={s.icon} label={s.label} color={s.color} size="small" sx={{ fontWeight: 600 }} />
                          </TableCell>
                          <TableCell sx={{ minWidth: 240 }}>
                            <Stepper activeStep={activeStep} alternativeLabel sx={{ "& .MuiStepIcon-root.Mui-active": { color: brand }, "& .MuiStepIcon-root.Mui-completed": { color: brand } }}>
                              {steps.map((label) => <Step key={label}><StepLabel>{label}</StepLabel></Step>)}
                            </Stepper>
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title={t("Details")}>
                              <IconButton onClick={() => openDetails(r)} sx={{ mr: .5 }}>
                                <InfoIcon />
                              </IconButton>
                            </Tooltip>

                            {/* Role-based actions */}
                            {canKatibuApprove && r.status === "PENDING_SECRETARY_APPROVAL" && (
                              <>
                                <Tooltip title={t("Approve")}>
                                  <IconButton onClick={() => handleReview(r, true, "Katibu")} color="success" sx={{ mr: .5 }}>
                                    <DoneAllIcon />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title={t("Reject")}>
                                  <IconButton onClick={() => handleReview(r, false, "Katibu")} color="error">
                                    <CloseIcon />
                                  </IconButton>
                                </Tooltip>
                              </>
                            )}

                            {canMuhasibuApprove && r.status === "PENDING_ACCOUNTANT_APPROVAL" && (
                              <>
                                <Tooltip title={t("Approve & Deduct")}>
                                  <IconButton onClick={() => handleReview(r, true, "Muhasibu")} color="success" sx={{ mr: .5 }}>
                                    <GavelIcon />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title={t("Reject")}>
                                  <IconButton onClick={() => handleReview(r, false, "Muhasibu")} color="error">
                                    <CloseIcon />
                                  </IconButton>
                                </Tooltip>
                              </>
                            )}
                          </TableCell>
                        </TableRow>
                      )
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </motion.div>

      {/* Initiate Dialog (Chairperson) */}
      <Dialog open={initiateOpen} onClose={() => setInitiateOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>
          {t("Initiate Emergency Contribution")}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: .5 }}>
            <Grid item xs={12} md={6}>
              <TextField
                label={t("Event Type")}
                fullWidth
                select
                value={form.type}
                onChange={(e) => setForm(f => ({ ...f, type: e.target.value }))}
              >
                <MenuItem value="Bereavement">{t("Bereavement")}</MenuItem>
                <MenuItem value="MEDICAL">{t("Medical")}</MenuItem>
                <MenuItem value="DISASTER">{t("Disaster")}</MenuItem>
                <MenuItem value="WEDDING">{t("Wedding")}</MenuItem>
                <MenuItem value="Other">{t("Other")}</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label={t("Deduction per Member (TZS)")}
                fullWidth
                value={form.deductionPerMember ? Number(form.deductionPerMember).toLocaleString('en-US') : ''}
                onChange={(e) => {
                  const val = e.target.value.replace(/,/g, "");
                  if (!isNaN(val)) {
                    setForm(f => ({ ...f, deductionPerMember: val }));
                  }
                }}
                InputProps={{
                  startAdornment: <InputAdornment position="start">TZS</InputAdornment>,
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label={t("Event Title")}
                placeholder={t("e.g., Msiba wa Mwanachama – Neema Peter")}
                fullWidth
                value={form.title}
                onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))}
              />
            </Grid>
          </Grid>
          <Divider sx={{ my: 2 }} />
          <Typography variant="body2" color="text.secondary">
            {t("After creation, the request will be sent to the Secretary and then the Accountant for approval before any deductions are made.")}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInitiateOpen(false)}>{t("Cancel")}</Button>
          <Button
            variant="contained"
            onClick={handleInitiate}
            sx={{ bgcolor: brand, "&:hover": { bgcolor: brandHover } }}
          >
            {t("Create")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Details Dialog */}
      <Dialog open={detailsOpen} onClose={closeDetails} fullWidth maxWidth="md">
        <DialogTitle>{t("Emergency Details")}</DialogTitle>
        <DialogContent dividers>
          {selectedEvent && (
            <>
              {/* Summary */}
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} md={8}>
                  <Typography variant="h6" sx={{ fontWeight: 800 }}>{selectedEvent.event_name}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t("Type:")} <b>{selectedEvent.event_type}</b> • {t("Deduction/Member:")} <b>{formatCurrency(selectedEvent.amount_per_member, locale)}</b> • {t("Initiated:")} {dayjs(selectedEvent?.created_at).format("DD MMM YYYY, HH:mm")}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                    {(() => {
                      const s = statusChip(selectedEvent.status);
                      return <Chip icon={s.icon} label={s.label} color={s.color} />;
                    })()}
                  </Box>
                </Grid>
              </Grid>

              {/* Chain tracker */}
              <Box sx={{ mb: 2 }}>
                <Stepper activeStep={getActiveStep(selectedEvent.status)} alternativeLabel
                  sx={{ "& .MuiStepIcon-root.Mui-active": { color: brand }, "& .MuiStepIcon-root.Mui-completed": { color: brand } }}>
                  {steps.map((label) => <Step key={label}><StepLabel>{label}</StepLabel></Step>)}
                </Stepper>
              </Box>

              <Grid container spacing={3}>
                {/* Affected Members (preview first 6 active members) */}
                <Grid item xs={12} md={7}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>{t("Affected Members (Preview)")}</Typography>
                      <Divider sx={{ mb: 1 }} />
                      <TableContainer component={Paper} elevation={0}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>#</TableCell>
                              <TableCell>{t("Member")}</TableCell>
                              <TableCell align="right">{t("Bal. Before")}</TableCell>
                              <TableCell align="right">{t("Bal. After*")}</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {selectedEvent.affected_members?.slice(0, 6).map((record, i) => {
                              return (
                                <TableRow key={record.id}>
                                  <TableCell>{i + 1}</TableCell>
                                  <TableCell>{record.member_name}</TableCell>
                                  <TableCell align="right">{formatCurrency(record.balance_before_deduction, locale)}</TableCell>
                                  <TableCell align="right">{formatCurrency(record.balance_after_deduction, locale)}</TableCell>
                                </TableRow>
                              )
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      <Typography variant="caption" color="text.secondary">
                        {t("Showing a historical snapshot of balances at the time of deduction.")}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Audit Trail */}
                <Grid item xs={12} md={5}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Audit Trail</Typography>
                      <Divider sx={{ mb: 1 }} />
                      <Stack spacing={1.2}>
                        {selectedEvent.audit?.map((a, idx) => (
                          <Box key={idx} sx={{ p: 1, borderRadius: 1, bgcolor: "#f9fafb", border: "1px solid #eee" }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {a.action} — <Typography component="span" variant="body2" color="text.secondary">{a.at}</Typography>
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {t("By:")} {a.by} • {a.note}
                            </Typography>
                          </Box>
                        ))}
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDetails}>{t("Close")}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
